import React from 'react';
import styled from '@emotion/styled';
import { RenderElementProps, useSelected } from "slate-react";
import { useState } from "react";
import { useConfigurableTable } from './useConfigurableTable';
import { usePreconfiguredFields } from '../CustomFieldsBlock/usePreconfiguredFields';
import { FormattedMessage } from 'react-intl';
import { usePowerEditorContext } from '../../slate/PowerEditorContext';
import { BlockSelectionCss } from '../../elements/Common';
import { Buttons, FormGrid, MoreActionsMenuButton, OccupyFreeSpace, Tooltip } from '../../../primitives';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { StrippedIconButton } from '../../../primitives/StrippedButtons';
import { Add, ExpandMore, ListAlt, TableRows, UnfoldLess } from '@mui/icons-material';
import { PreconfiguredEditor } from './PreconfiguredEditor';
import isHotkey from 'is-hotkey';



export const ConfigurableTableBlockElementType = "configurable_table_block";


export const ConfigurableTableBlockElement = (props: RenderElementProps) => {
  const { element, children } = props;
  
  const data = useConfigurableTable(element as any);
  const {
    rows,
    insertRow,
    updateRow,
    removeRow,
    updateCollapsed,
    moveRow,
    isCollapsed,
  } = data;
  const isSelected = useSelected();

  const preconfigured = usePreconfiguredFields((element as any).preconfigured_fields);

  const { viewMode } = usePowerEditorContext();

  const [dragging,setDragging] = useState<string | null>(null);
  const [dragTarget,setDragTarget] = useState<string | null>(null);

  const blockTitle = (element as any).block_title || (element as any).title || (element as any).block_type;

  const rowActions = (r: any, idx: number): any => ([
    ["remove", <FormattedMessage id="common.delete"/>, () => removeRow(r._id)],
    ["row_before", <FormattedMessage id="powerdoc.plugins.table.insert.row_before"/>, () => insertRow(idx)],
    ["row_after", <FormattedMessage id="powerdoc.plugins.table.insert.row_after"/>, () => insertRow(idx+1)],
    ["duplicate", <FormattedMessage id="powerdoc.plugins.configurable_table_block.actions.duplicate_row"/>, () => data.duplicateRow(r._id)],
  ])

  return (
    <div {...props.attributes}>
      {props.children}

      <Wrapper contentEditable={false} isSelected={isSelected}>
        {children}

        {isCollapsed && <>
          <Buttons style={{ paddingTop: 0 }}>
            {!!blockTitle &&
              <Typography onClick={() => updateCollapsed(false)}>{blockTitle}</Typography>}
            <OccupyFreeSpace />

            <Tooltip text_id="common.show_details">
              <StrippedIconButton onClick={() => updateCollapsed(false)}><ExpandMore /></StrippedIconButton>
            </Tooltip>
          </Buttons>
        </>}

        {!isCollapsed && <>
          {!data.isForms && <TableContainer>
            <Table size="small">

              <TableHead>
                <TableCell className="idx" width={40}></TableCell>
                {!!preconfigured.fields.length && preconfigured.fields.map(f => (
                  <TableCell key={f.code}>
                    <Tooltip text={f.code}>
                      <>{f.label}</>
                    </Tooltip>
                  </TableCell>
                  
                ))}

                <TableCell width={40}></TableCell>
              </TableHead>

              <TableBody>
                {rows.map((r,idx) => (
                  <TableRow
                    key={r._id}
                    className={`tr ${r._id === dragging ? "dragged" : ""} ${r._id === dragTarget ? "drag-targetd" : ""}`}
                    draggable={!viewMode}
                    onDragStart={() => setDragging(r._id)}
                    onDragEnter={e => { e.preventDefault(); setDragTarget(r._id)}}
                    onDragOver={e => { e.preventDefault(); setDragTarget(r._id)}}
                    onDragLeave={e => { e.preventDefault(); setDragTarget(null)}}
                    onKeyUp={e => {
                      if(isHotkey("mod+Enter", e)) {
                        insertRow(idx+1);
                      }
                    }}
                    onDrop={e => {
                      e.stopPropagation();
                      if(dragging && r._id !== dragging) {
                        const idxFrom = rows.findIndex(rx => rx._id === dragging);
                        if(idxFrom >= 0 && idxFrom !== idx) {
                          moveRow(idxFrom, idx);
                        }
                      }
                      setDragging(null);
                      setDragTarget(null);
                    }}
                    >
                    <TableCell className="idx" width={40}>
                      <Typography variant="caption" color="textSecondary">{idx+1}</Typography>
                    </TableCell>

                    {!!preconfigured.fields.length && preconfigured.fields.map((f,fi) => (
                      <TableCell key={f.code}>
                        <PreconfiguredEditor
                          field={f}
                          schema={preconfigured.schema}
                          row={r}
                          updateRow={updateRow}
                          autoFocus={fi === 0 && data.insertedId === r._id}
                          />
                      </TableCell>
                      
                    ))}

                    <TableCell width={40} className="actions">
                      <MoreActionsMenuButton
                        actions={rowActions(r,idx)}
                        strippedButton
                        />
                    </TableCell>
                  </TableRow>
                ))}
                
              </TableBody>
            </Table>
          </TableContainer>}

          {!!data.isForms &&
            <FormGrid columns="1fr" className="rows-forms">

              {rows.map((r,idx) => (
                <FormGrid key={r._id} columns="max-content 1fr" noMargin className="row-form">

                  {!!preconfigured.fields.length && preconfigured.fields.map((f,fi) => (
                    <React.Fragment key={f.code}>
                      <Tooltip text={f.code}>
                        <Typography color="textSecondary">{f.label}</Typography>
                      </Tooltip>
                      <PreconfiguredEditor
                        field={f}
                        schema={preconfigured.schema}
                        row={r}
                        updateRow={updateRow}
                        autoFocus={fi === 0 && data.insertedId === r._id}
                        />
                    </React.Fragment>
                    
                  ))}

                  <Typography className="idx" variant="caption" color="textSecondary">{idx+1}</Typography>

                  <MoreActionsMenuButton
                    className="actions"
                    actions={rowActions(r,idx)}
                    strippedButton
                    />
                  </FormGrid>
                ))}
            </FormGrid>}
          
          <Buttons className="tail-actions">
            <StrippedIconButton onClick={() => insertRow()}><Add /></StrippedIconButton>
            <StrippedIconButton onClick={() => data.updateIsForms(!data.isForms)}>{data.isForms ? <TableRows /> : <ListAlt />}</StrippedIconButton>
            <StrippedIconButton onClick={() => updateCollapsed(true)}><UnfoldLess /></StrippedIconButton>
          </Buttons>
        </>}


      </Wrapper>
    </div>
  );
}



const Wrapper = styled.div<{ isSelected?: boolean }>`
  padding: 0.5rem 1rem;
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  ${props => BlockSelectionCss(props)}

  & .tail-actions {
    width: 100%;
    justify-content: center;
  }

  & .tr {
    & .idx {
      vertical-align: top;
    }
    & .actions {
      opacity: 0;
    }
    &:hover {
      & .actions {
        opacity: 1;
      }
    }
  }

  & .row-form {
    position: relative;
    padding: 0.5rem 0.5rem;
    align-items: flex-end;

    &:nth-child(2n) {
      background: rgba(0,0,0,0.08);
    }
    &:nth-child(2n+1) {
      background: rgba(0,0,0,0.03);
    }

    & .idx {
      position: absolute;
      top: 2px;
      left: 0.5rem;
      transform-origin: top left;
      transform: scale(0.75);
    }
    & .actions {
      position: absolute;
      top: 2px;
      right: 0.5rem;
      opacity: 0;
    }
    &:hover {
      & .actions {
        opacity: 1;
      }
    }
  }
`;
